// @greenlight-spec: 07-source-control-pipeline.md §GitHub App creation flow
// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { StepHeader } from '@/components/install/StepHeader';
import { api } from '@/lib/api';
import { errorBodyCode, errorBodyMessage, thrownCode, thrownMessage } from '@/lib/apiErrors';
import { submitManifestIfPresent } from '@/lib/githubManifest';
import { isValidGithubOrgLogin } from '@greenlight/shared';
import type { InstallationState } from '@greenlight/shared';
import type { InstallScmInstallation } from '@greenlight/contract';

/** Result of the wizard's advance-step call: `code` lets this step show a
 * recovery action specific to why the server refused, not just that it did. */
interface AdvanceStepResult {
  ok: boolean;
  code?: string;
}

interface GitHubAppStepProps {
  installationState: InstallationState;
  /** The org's SCM connection, or null until GitHub reports the App installed. */
  scmInstallation: InstallScmInstallation | null;
  blocked: boolean;
  /** True when this screen is the wizard's next required step — the installation
   * poll runs only then, so a preview visit issues no background requests. */
  active: boolean;
  onNext: () => Promise<AdvanceStepResult>;
  onReset: () => Promise<void>;
  /** Re-reads install status — the manual companion to the wizard's focus refetch. */
  onRefresh: () => Promise<void>;
  isSubmitting: boolean;
}

interface GithubAppDetails {
  app_id?: string;
  app_slug?: string;
  target_github_org?: string;
}

const INSTALLATION_POLL_MS = 3_000;

export function GitHubAppStep({
  installationState,
  scmInstallation,
  blocked,
  active,
  onNext,
  onReset,
  onRefresh,
  isSubmitting,
}: GitHubAppStepProps) {
  const details = (installationState.steps?.github_app?.details ?? {}) as GithubAppDetails;
  const alreadyCreated = Boolean(details.app_id && details.app_slug);
  const connected = scmInstallation !== null && !scmInstallation.suspended;
  // App creation alone leaves the step `running` with no scm row — Continue
  // stays held. A prior true completion (complete + any scm row, including a
  // later suspension) stays passable so revisiting does not strand IT.
  const canContinue =
    connected ||
    (installationState.steps?.github_app?.status === 'complete' && scmInstallation !== null);

  const [githubOrg, setGithubOrg] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  // The server checks the typed login exists on GitHub; that answer belongs at
  // the field, with the form intact, not in a page-level alert.
  const [orgNotFound, setOrgNotFound] = React.useState<string | null>(null);
  const [resetError, setResetError] = React.useState<string | null>(null);
  const [nextError, setNextError] = React.useState<string | null>(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const beginMutation = api.install.beginGithubApp.useMutation();
  const resetMutation = api.install.resetGithubApp.useMutation();

  const trimmedOrg = githubOrg.trim();
  const orgFormatValid = trimmedOrg !== '' && isValidGithubOrgLogin(trimmedOrg);
  const showOrgFormatError = trimmedOrg !== '' && !orgFormatValid;
  const targetOrg =
    scmInstallation?.orgHandle ??
    (typeof details.target_github_org === 'string' && details.target_github_org.length > 0
      ? details.target_github_org
      : null);

  const handleCreate = async () => {
    if (!orgFormatValid) return;
    setError(null);
    setOrgNotFound(null);
    try {
      const result = await beginMutation.mutateAsync({ body: { githubOrg: trimmedOrg } });
      if (result.status !== 200) {
        const message = errorBodyMessage(result.body, 'Could not start GitHub App creation.');
        if (errorBodyCode(result.body) === 'scm.github_org_not_found') setOrgNotFound(message);
        else setError(message);
        return;
      }
      if (result.body.alreadyInstalled) {
        // Server detected an existing App between render and click — fall through
        // to the install link rather than creating a duplicate.
        if (result.body.installUrl) window.location.href = result.body.installUrl;
        return;
      }
      submitManifestIfPresent(result.body);
    } catch (err) {
      const message = thrownMessage(
        err,
        'Could not reach the control plane. Check it is running and try again.',
      );
      if (thrownCode(err) === 'scm.github_org_not_found') setOrgNotFound(message);
      else setError(message);
    }
  };

  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  // Keep checking while GitHub has not reported the install — focus-refetch alone
  // misses the case where IT stays on this tab after installing in another.
  React.useEffect(() => {
    if (!active || !alreadyCreated || connected || blocked) return;
    const id = window.setInterval(() => {
      void handleRefresh();
    }, INSTALLATION_POLL_MS);
    return () => window.clearInterval(id);
  }, [active, alreadyCreated, blocked, connected, handleRefresh]);

  const handleNext = async () => {
    setNextError(null);
    // The server re-checks the installation, so a connection that lapsed between
    // the last refetch and this click still fails closed rather than advancing.
    const result = await onNext();
    if (!result.ok) {
      setNextError(
        result.code === 'scm.installation_invalid'
          ? 'This installation no longer matches Greenlight\'s stored App credentials. Choose "Start over" below, then reinstall the App into your organization.'
          : 'Greenlight could not confirm the GitHub App installation. Install the App into your GitHub org, then check again.',
      );
    }
  };

  const handleReset = async () => {
    setResetError(null);
    try {
      const result = await resetMutation.mutateAsync({ body: {} });
      if (result.status !== 200) {
        setResetError(errorBodyMessage(result.body, 'Could not reset the GitHub App step.'));
        return;
      }
      await onReset();
    } catch (err) {
      setResetError(
        thrownMessage(err, 'Could not reach the control plane. Check it is running and try again.'),
      );
    }
  };

  return (
    <div className="space-y-6" data-testid="step-github_app">
      <StepHeader step="github_app" title="GitHub App">
        Greenlight creates a customer-specific GitHub App for your organization, then you install it
        so Greenlight can manage repositories.
      </StepHeader>

      {blocked ? (
        <div
          className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
          data-testid="step-blocked"
        >
          This step is blocked until the verified-URL handoff is complete.
        </div>
      ) : alreadyCreated ? (
        <div className="space-y-4" data-testid="github-app-created">
          <dl className="divide-y divide-border rounded-lg border text-sm">
            <div className="flex flex-col sm:flex-row sm:items-center px-4 py-3 gap-1">
              <dt className="text-muted-foreground w-full sm:w-40 shrink-0">GitHub App</dt>
              <dd className="font-medium min-w-0">
                <MonoValue copyable wrap>
                  {details.app_slug!}
                </MonoValue>
              </dd>
            </div>
            {targetOrg && (
              <div className="flex flex-col sm:flex-row sm:items-center px-4 py-3 gap-1">
                <dt className="text-muted-foreground w-full sm:w-40 shrink-0">Organization</dt>
                <dd className="min-w-0" data-testid="github-app-target-org">
                  <MonoValue>{targetOrg}</MonoValue>
                </dd>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center px-4 py-3 gap-1">
              <dt className="text-muted-foreground w-full sm:w-40 shrink-0">Installation</dt>
              <dd className="min-w-0" data-testid="github-app-installation">
                {connected ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <StatusBadge variant="active">Installed</StatusBadge>
                    <MonoValue>{scmInstallation.orgHandle}</MonoValue>
                  </span>
                ) : scmInstallation ? (
                  <StatusBadge variant="failed">Suspended</StatusBadge>
                ) : (
                  <span className="flex flex-wrap items-center gap-2 text-muted-foreground">
                    <Loader2
                      className="size-4 shrink-0 animate-spin"
                      aria-hidden="true"
                      data-testid="github-app-installation-spinner"
                    />
                    <StatusBadge variant="pending">Waiting for GitHub</StatusBadge>
                  </span>
                )}
              </dd>
            </div>
          </dl>
          <p className="text-sm text-muted-foreground" data-testid="github-app-installation-hint">
            {connected
              ? 'Connected — you can continue to create the Greenlight agent plugin repo.'
              : scmInstallation
                ? 'This installation was uninstalled or suspended on GitHub. Install the App again to reconnect it, or start over with a different App.'
                : targetOrg
                  ? `The App has been created. Install it into ${targetOrg} — this page checks automatically until GitHub reports the installation.`
                  : 'The App has been created. Install it into your GitHub org — this page checks automatically until GitHub reports the installation.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            {/* One filled primary per screen: installing the App while it is
                still unconnected, Continue once GitHub reports it. */}
            <Button
              asChild
              variant={connected ? 'outline' : 'default'}
              className="w-full sm:w-auto h-11 md:h-9"
            >
              <a
                href={`https://github.com/apps/${details.app_slug}/installations/new`}
                target="_blank"
                rel="noreferrer"
                data-testid="github-app-install-link"
              >
                {connected
                  ? 'Manage the installation on GitHub'
                  : 'Install the App into your GitHub org'}
              </a>
            </Button>
            {!connected && (
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto h-11 md:h-9"
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="github-app-installation-refresh"
              >
                {refreshing ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Checking…
                  </span>
                ) : (
                  'Check again'
                )}
              </Button>
            )}
            <Button
              type="button"
              variant={canContinue ? 'default' : 'outline'}
              className="w-full sm:w-auto h-11 md:h-9"
              onClick={handleNext}
              disabled={isSubmitting || !canContinue}
              data-testid="step-next"
            >
              {isSubmitting ? 'Saving…' : 'Continue'}
            </Button>
          </div>
          {nextError && (
            <Alert variant="destructive" data-testid="github-app-next-error">
              <AlertDescription>{nextError}</AlertDescription>
            </Alert>
          )}
          {resetError && (
            <Alert variant="destructive" data-testid="github-app-reset-error">
              <AlertDescription>{resetError}</AlertDescription>
            </Alert>
          )}
          <Button
            type="button"
            variant="ghost"
            className="px-0 text-muted-foreground"
            onClick={handleReset}
            disabled={resetMutation.isPending}
            data-testid="github-app-reset"
          >
            {resetMutation.isPending ? 'Starting over…' : 'Start over with a different App'}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="github-org">GitHub organization</Label>
            <Input
              id="github-org"
              placeholder="my-org"
              value={githubOrg}
              onChange={(e) => {
                setGithubOrg(e.target.value);
                setOrgNotFound(null);
              }}
              aria-invalid={showOrgFormatError || orgNotFound !== null}
              data-testid="github-org-input"
            />
            <p className="text-xs text-muted-foreground">
              The GitHub organization to create the App in. Greenlight requires a GitHub
              organization; personal accounts are not supported.
            </p>
            {showOrgFormatError && (
              <p className="text-xs text-destructive" data-testid="github-org-format-error">
                Enter a valid GitHub organization login (letters, digits, and hyphens; no
                leading/trailing hyphen).
              </p>
            )}
            {orgNotFound && (
              <p className="text-xs text-destructive" data-testid="github-org-not-found">
                {orgNotFound}
              </p>
            )}
          </div>

          {error && (
            <Alert variant="destructive" data-testid="github-app-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            type="button"
            className="w-full md:w-auto h-11 md:h-9"
            onClick={handleCreate}
            disabled={beginMutation.isPending || !orgFormatValid}
            data-testid="github-app-create"
          >
            {beginMutation.isPending
              ? 'Starting…'
              : trimmedOrg
                ? `Create GitHub App in ${trimmedOrg}`
                : 'Create GitHub App'}
          </Button>
        </div>
      )}
    </div>
  );
}
